import { useCurrentUserContext } from "@/features/users/hooks/useCurrentUserContext";

import { useAuth } from "./useAuth";

import type { User } from "../../users/schemas/user";

/**
 * Datos del usuario autenticado: la parte de Firebase Auth y la de Firestore.
 *
 * OJO: los indicadores que expone son comprobaciones de TIPO DE DATO, no de
 * autorización. Antes existía aquí un `isAdmin` que significaba
 * `type === "admin"` y se usaba para decidir si mostrar acciones; se eliminó
 * porque ignoraba los permisos reales y discrepaba de todos los demás gates.
 * Para autorizar, usa `usePermissions()`.
 *
 * @example
 * ```typescript
 * const { userData, isLoading, isInstitution } = useCurrentUserData();
 * const { can } = usePermissions(); // para decidir permisos
 * ```
 */
export function useCurrentUserData() {
  const { currentUser: authUser, loading: authLoading } = useAuth();
  const {
    currentUserData: userData,
    loading: userDataLoading,
    error: userDataError,
  } = useCurrentUserContext();

  const isLoading = authLoading || userDataLoading;
  const hasUserData = !!userData;
  const isAuthenticated = !!authUser;

  // Comprobaciones del tipo de cuenta en la plataforma médica. Describen qué
  // ES el usuario, no qué PUEDE hacer.
  const isInstitution = userData?.type === "institution";
  const isPhysician = userData?.type === "physician";
  const isPatient = userData?.type === "patient";

  return {
    // User data
    authUser,
    userData,

    // Loading states
    isLoading,
    authLoading,
    userDataLoading,

    // Status flags
    isAuthenticated,
    hasUserData,

    // Type checks
    isInstitution,
    isPhysician,
    isPatient,

    // Error handling
    userDataError,

    // Utility functions
    hasType: (type: User["type"]) => userData?.type === type,
  };
}
